import dictionary from '@repo/secoda-editor/dictionary';
import type { SecodaEditorComponentProps } from '@repo/secoda-editor/types';
import { ToastType } from '@repo/secoda-editor/types';
import copy from 'copy-to-clipboard';
import { NodeSelection } from 'prosemirror-state';
import { memo, useCallback } from 'react';
import type { MetricNumericFormat } from '../../../../../api';
import { ChartBlockContainer } from '../components/ChartBlock/ChartBlockContainer';
import type { CreateGraphModalResult } from '../components/ChartBlock/CreateGraphModal';

export const ChartBlockContainerWrapper = memo(
	({
		node,
		getPos,
		isSelected,
		view,
		readOnly,
		onShowToast,
		onTrackEvent,
	}: SecodaEditorComponentProps & {
		readOnly: boolean;
		onShowToast?: (message: string, type: ToastType) => void;
		onTrackEvent?: (
			eventName: string,
			properties?: Record<string, string>
		) => void;
	}) => {
		const onDelete = useCallback(() => {
			const $pos = view.state.doc.resolve(getPos());
			const tr = view.state.tr.setSelection(new NodeSelection($pos));
			view.dispatch(tr.deleteSelection());
			view.focus();
			onShowToast?.(dictionary.chartDeleted, ToastType.Info);
		}, [getPos, onShowToast, view]);

		const onDuplicate = useCallback(() => {
			const emptyParagraph = view.state.schema.nodes['paragraph'].create();
			const pos = getPos();

			view.dispatch(
				view.state.tr
					.insert(
						pos + node.nodeSize, // after the current query block
						emptyParagraph
					)
					.insert(
						pos + node.nodeSize + emptyParagraph.nodeSize, // after the empty paragraph created above
						node.type.create(node.attrs)
					)
			);
			view.focus();
			onShowToast?.(dictionary.chartDuplicated, ToastType.Info);
		}, [getPos, node, onShowToast, view]);

		const onCopyLink = useCallback(() => {
			const hash = `#metric-chart-${node.attrs.id}`;

			onTrackEvent?.('editor/chart-block-copy-link');

			// The existing url might contain a hash already, lets make sure to remove
			// that rather than appending another one.
			const urlWithoutHash = window.location.href.split('#')[0];
			copy(urlWithoutHash + hash);
			onShowToast?.(dictionary.linkCopied, ToastType.Info);
		}, [node.attrs.id, onShowToast, onTrackEvent]);

		const onSave = useCallback(
			async (result: CreateGraphModalResult) => {
				const { xAxis, yAxis, dimension, numericFormat } = result;
				const pos = getPos();

				const tr = view.state.tr.setNodeMarkup(pos, null, {
					...node.attrs,
					xAxis,
					yAxis,
					dimension,
					numericFormat,
				});
				view.dispatch(tr);
			},
			[getPos, node.attrs, view]
		);

		return (
			<ChartBlockContainer
				id={node.attrs.id}
				readOnly={readOnly}
				onDelete={onDelete}
				isSelected={isSelected}
				onDuplicate={onDuplicate}
				onCopyLink={onCopyLink}
				onSave={onSave}
				xAxis={node.attrs.xAxis}
				yAxis={node.attrs.yAxis}
				dimension={node.attrs.dimension}
				numericFormat={node.attrs.numericFormat as MetricNumericFormat}
			/>
		);
	}
);
